/**
 * Action Feedback Manager
 * Handles submission and retrieval of action feedback for reinforcement learning
 */

import apis from '@api/api';

// Feedback score cache
const feedbackScoreCache = new Map<string, any>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface FeedbackSubmission {
	actionIntent: string;
	query: string;
	feedbackType: 'upvote' | 'downvote';
	wasExecuted?: boolean;
	executionSuccess?: boolean;
	context?: {
		screen?: string;
		time?: Date;
		companyId?: string;
		additionalData?: any;
	};
	confidence?: number;
	executionTime?: number;
	errorMessage?: string;
}

interface FeedbackScores {
	actionIntent: string;
	totalUpvotes: number;
	totalDownvotes: number;
	totalExecutions: number;
	successfulExecutions: number;
	avgConfidence: number;
	lastFeedback: Date | null;
	feedbackCount: number;
	score: number;
	successRate: number;
}

interface UserPreferences {
	actionIntent: string;
	score: number;
	upvotes: number;
	downvotes: number;
	executions: number;
	lastUsed: Date;
	avgConfidence: number;
	feedbackMultiplier: number;
	recencyWeight: number;
	frequencyBonus: number;
}

/**
 * Submit action feedback
 */
export const submitFeedback = async (
	feedback: FeedbackSubmission
): Promise<boolean> => {
	try {
		const response = await apis.post('/action-feedback', feedback);

		if (response.success) {
			// Invalidate cache for this action
			invalidateCache(feedback.actionIntent);
			return true;
		}

		return false;
	} catch (error) {
		console.error('Error submitting feedback:', error);
		return false;
	}
};

/**
 * Get feedback scores for actions (with caching)
 */
export const getFeedbackScores = async (
	userId?: string,
	actionIntent?: string
): Promise<FeedbackScores[] | FeedbackScores | null> => {
	try {
		const cacheKey = `scores_${userId || 'global'}_${actionIntent || 'all'}`;
		const cached = feedbackScoreCache.get(cacheKey);

		if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
			return cached.data;
		}

		const params = new URLSearchParams();
		if (userId) params.append('userSpecific', 'true');
		if (actionIntent) params.append('actionIntent', actionIntent);

		const url = `/action-feedback/scores${params.toString() ? '?' + params.toString() : ''}`;
		const response = await apis.get(url);

		if (response.success) {
			const data = response.data;

			// Cache the result
			feedbackScoreCache.set(cacheKey, {
				data,
				timestamp: Date.now(),
			});

			return data;
		}

		return null;
	} catch (error) {
		console.error('Error fetching feedback scores:', error);
		return null;
	}
};

/**
 * Get user-specific action preferences
 */
export const getUserPreferences = async (
	userId?: string,
	limit: number = 50
): Promise<UserPreferences[] | null> => {
	try {
		const cacheKey = `preferences_${userId || 'global'}_${limit}`;
		const cached = feedbackScoreCache.get(cacheKey);

		if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
			return cached.data;
		}

		const params = new URLSearchParams();
		params.append('limit', limit.toString());
		if (userId) params.append('includeGlobal', 'false');

		const url = `/action-feedback/user-preferences${params.toString() ? '?' + params.toString() : ''}`;
		const response = await apis.get(url);

		if (response.success) {
			const data = response.data.userPreferences;

			// Cache the result
			feedbackScoreCache.set(cacheKey, {
				data,
				timestamp: Date.now(),
			});

			return data;
		}

		return null;
	} catch (error) {
		console.error('Error fetching user preferences:', error);
		return null;
	}
};

/**
 * Calculate feedback multiplier for an action
 */
export const calculateFeedbackMultiplier = (
	upvotes: number,
	downvotes: number,
	recency: Date | null,
	frequency: number
): number => {
	// Base score: (upvotes - downvotes) / (upvotes + downvotes + 1)
	const baseScore = (upvotes - downvotes) / (upvotes + downvotes + 1);

	// Recency weight: exponential decay based on timestamp
	const recencyWeight = calculateRecencyWeight(recency);

	// Frequency bonus: log(executionCount) * 0.1
	const frequencyBonus = Math.log(frequency + 1) * 0.1;

	// Final multiplier: 0.5 + (baseScore * 0.5) + recencyWeight + frequencyBonus
	const multiplier = 0.5 + baseScore * 0.5 + recencyWeight + frequencyBonus;

	// Clamp between 0.3 and 2.0
	return Math.max(0.3, Math.min(2.0, multiplier));
};

/**
 * Calculate recency weight
 */
function calculateRecencyWeight(timestamp: Date | null): number {
	if (!timestamp) return 0;

	const now = new Date();
	const daysDiff =
		(now.getTime() - timestamp.getTime()) / (1000 * 60 * 60 * 24);

	// Exponential decay: more recent feedback has higher weight
	return Math.exp(-daysDiff / 30) * 0.2; // 30-day half-life, max 0.2 bonus
}

/**
 * Get feedback multiplier for a specific action
 */
export const getFeedbackMultiplier = async (
	actionIntent: string,
	userId?: string
): Promise<number> => {
	try {
		const preferences = await getUserPreferences(userId);

		if (!preferences) return 1.0;

		const actionPreference = preferences.find(
			(pref) => pref.actionIntent === actionIntent
		);

		if (!actionPreference) return 1.0;

		return actionPreference.feedbackMultiplier;
	} catch (error) {
		console.error('Error getting feedback multiplier:', error);
		return 1.0;
	}
};

/**
 * Batch submit feedback for multiple actions
 */
export const submitBatchFeedback = async (
	feedbackList: FeedbackSubmission[]
): Promise<boolean[]> => {
	const results = await Promise.allSettled(
		feedbackList.map((feedback) => submitFeedback(feedback))
	);

	return results.map((result) =>
		result.status === 'fulfilled' ? result.value : false
	);
};

/**
 * Get feedback analytics
 */
export const getFeedbackAnalytics = async (
	timeRange: '7d' | '30d' | '90d' = '30d'
) => {
	try {
		const response = await apis.get(
			`/action-feedback/analytics?timeRange=${timeRange}`
		);

		if (response.success) {
			return response.data;
		}

		return null;
	} catch (error) {
		console.error('Error fetching feedback analytics:', error);
		return null;
	}
};

/**
 * Clear feedback cache
 */
export const clearFeedbackCache = (actionIntent?: string) => {
	if (actionIntent) {
		// Clear specific action cache
		for (const [key] of feedbackScoreCache.entries()) {
			if (key.includes(actionIntent)) {
				feedbackScoreCache.delete(key);
			}
		}
	} else {
		// Clear all cache
		feedbackScoreCache.clear();
	}
};

/**
 * Invalidate cache for specific action
 */
function invalidateCache(actionIntent: string) {
	for (const [key] of feedbackScoreCache.entries()) {
		if (key.includes(actionIntent)) {
			feedbackScoreCache.delete(key);
		}
	}
}

/**
 * Get cached feedback multiplier (for performance)
 */
export const getCachedFeedbackMultiplier = (
	actionIntent: string,
	userId?: string
): number => {
	const cacheKey = `preferences_${userId || 'global'}_50`;
	const cached = feedbackScoreCache.get(cacheKey);

	if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
		const actionPreference = cached.data.find(
			(pref: UserPreferences) => pref.actionIntent === actionIntent
		);

		return actionPreference?.feedbackMultiplier || 1.0;
	}

	return 1.0;
};

/**
 * Preload feedback data for common actions
 */
export const preloadFeedbackData = async (
	actionIntents: string[],
	userId?: string
) => {
	try {
		const preferences = await getUserPreferences(userId);

		if (preferences) {
			// Cache individual action multipliers for quick access
			actionIntents.forEach((intent) => {
				const preference = preferences.find(
					(pref) => pref.actionIntent === intent
				);
				if (preference) {
					const cacheKey = `multiplier_${userId || 'global'}_${intent}`;
					feedbackScoreCache.set(cacheKey, {
						data: preference.feedbackMultiplier,
						timestamp: Date.now(),
					});
				}
			});
		}
	} catch (error) {
		console.error('Error preloading feedback data:', error);
	}
};

export default {
	submitFeedback,
	getFeedbackScores,
	getUserPreferences,
	calculateFeedbackMultiplier,
	getFeedbackMultiplier,
	submitBatchFeedback,
	getFeedbackAnalytics,
	clearFeedbackCache,
	getCachedFeedbackMultiplier,
	preloadFeedbackData,
};
